// Recurso EXPORTACAO = Recurso + Comando de destino + PublicacaoConfig:
//  - mapeamentoRecebimentoId: FONTE dos registros transformados a exportar (mapeamento do PROCESSAMENTO)
//  - comandoDestinoId: comando executado 1x por registro (SQL ou API), com template ${campo}
//  - retornoIntegracao: true -> exporta os registros RETORNAR (confirmação); false -> exporta PENDENTE + ERRO
//  - retornarIntegracao: true -> depois de exportar marca RETORNAR em vez de INTEGRADO

export type OrigemCampo = 'MAPEAMENTO' | 'JOIN' | 'AGRUPAMENTO'
export type CampoDisponivel = { campo: string; origem: OrigemCampo }

export type RecursoAnterior = {
  tipoRecurso: string;
  ordemExecucao: number;
  comandoMapeamentoId?: number | null;
  configuracoes?: Record<string, any> | null;
}

/** Escreve o placeholder ${campo}; as chaves são montadas aqui porque o template não pode ter essa sequência literal */
export const placeholder = (campo: string): string => '$' + '{' + campo + '}';

const NOME_PLACEHOLDER = /\$\{([^}]+)\}/g;

/** Nomes usados no texto como ${nome} (SQL, corpo JSON ou SOAP) */
export function placeholdersUsados(texto: string | null | undefined): string[] {
  const nomes = new Set<string>();
  let m: RegExpExecArray | null;
  const re = new RegExp(NOME_PLACEHOLDER);
  while ((m = re.exec(texto ?? '')) !== null) nomes.add(m[1].trim());
  return [...nomes];
}

/** Placeholders usados no comando que nenhum campo disponível, nem parâmetro do comando, fornece */
export function placeholdersDesconhecidos(texto: string | null | undefined, campos: CampoDisponivel[], parametros?: Record<string, any> | null): string[] {
  const conhecidos = new Set([...campos.map(c => c.campo.toLowerCase()), ...Object.keys(parametros ?? {}).map(k => k.toLowerCase())]);
  return placeholdersUsados(texto).filter(n => !conhecidos.has(n.toLowerCase()));
}

/**
 * Campos que o registro exportado terá: os destinos do mapeamento de origem e o que JOINs e AGRUPAMENTOs
 * anteriores (sobre o mesmo mapeamento) acrescentam.
 */
export function camposDisponiveis(
  mapeamentos: Record<string, any> | null | undefined,
  mapeamentoId: number | null | undefined,
  anteriores: RecursoAnterior[]
): CampoDisponivel[] {
  const vistos = new Set<string>();
  const saida: CampoDisponivel[] = [];
  const add = (campo: unknown, origem: OrigemCampo) => {
    const nome = typeof campo === 'string' ? campo.trim() : '';
    if (!nome || vistos.has(nome.toLowerCase())) return;
    vistos.add(nome.toLowerCase());
    saida.push({ campo: nome, origem });
  };
  // no mapeamento a chave é o campo de origem e o valor é o de destino: o registro transformado tem os destinos
  Object.values(mapeamentos ?? {}).forEach(destino => add(destino, 'MAPEAMENTO'));
  if (mapeamentoId) {
    for (const r of anteriores.filter(x => x.comandoMapeamentoId === mapeamentoId).sort((a, b) => a.ordemExecucao - b.ordemExecucao)) {
      if (r.tipoRecurso === 'JOIN') {
        (r.configuracoes?.['join']?.campos ?? []).forEach((c: any) => add(c?.destino, 'JOIN'));
      } else if (r.tipoRecurso === 'AGRUPAMENTO') {
        (r.configuracoes?.['agrupamento']?.tratamentos ?? []).forEach((t: any) => add(t?.nomeDestino || t?.nomeCampo, 'AGRUPAMENTO'));
      }
    }
  }
  return saida;
}

/** Explicação em uma frase do que as duas flags produzem juntas */
export function descreverRetorno(retornoIntegracao: boolean, retornarIntegracao: boolean): string {
  const quem = retornoIntegracao ? 'os registros com status RETORNAR (confirmação de uma integração anterior)' : 'os registros PENDENTES e com ERRO';
  const depois = retornarIntegracao ? 'marca cada registro enviado como RETORNAR, à espera de um recurso de retorno' : 'marca cada registro enviado como INTEGRADO';
  return `Envia ${quem} e, depois de enviar, ${depois}.`;
}

/** Primeiro problema da parte específica da exportação, ou null */
export function validarExportacao(config: { mapeamentoRecebimentoId?: number | null } | null | undefined): string | null {
  if (!config?.mapeamentoRecebimentoId) {
    return 'Selecione o mapeamento de origem (fonte dos registros a exportar). Crie antes um recurso de Processamento neste fluxo, se ainda não houver.';
  }
  return null;
}
